/**
 * NowExpress — Lab Picker Modal (nx-lab-picker.js)
 *
 * Opens an overlay modal when a diagnostics test has 2+ partner labs.
 * Customer selects a clinic (or "Select Nearest" via geolocation).
 * Returns the selected lab ID to the caller via an onSelect callback.
 *
 * Usage:
 *   window.NXLabPicker.open({
 *     partnerLabs: ['clinic_a', 'clinic_b'],
 *     clinicDirectory: { clinics: [...] },   // window.__NX_CLINIC_DATA
 *     onSelect: function(labId) { ... }
 *   });
 */
(function () {
  'use strict';

  var state = {
    overlay: null,
    modal: null,
    currentLabs: [],
    currentClinics: [],
    selectedLab: null,
    geoCoords: null,
    geoGranted: false,
    selectedBranchIdx: -1,
    onSelect: null
  };

  // ── Haversine distance (km) ─────────────────────────────────────────────────

  function haversineDistance(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLng = (lng2 - lng1) * Math.PI / 180;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  // ── DOM setup ───────────────────────────────────────────────────────────────

  function ensureModal() {
    if (state.overlay) return;

    state.overlay = document.createElement('div');
    state.overlay.id = 'nx-lab-picker-overlay';
    state.overlay.style.cssText = [
      'position:fixed;inset:0;z-index:9999',
      'background:rgba(0,0,0,.55)',
      'display:flex;align-items:center;justify-content:center',
      'opacity:0;transition:opacity .2s;pointer-events:none'
    ].join(';');

    state.modal = document.createElement('div');
    state.modal.id = 'nx-lab-picker-modal';
    state.modal.setAttribute('role', 'dialog');
    state.modal.setAttribute('aria-modal', 'true');
    state.modal.setAttribute('aria-label', 'Select a lab');
    state.modal.style.cssText = [
      'background:#fff;border-radius:16px;padding:24px 28px',
      'max-width:520px;width:90%;max-height:80vh;overflow-y:auto',
      'box-shadow:0 12px 40px rgba(0,0,0,.25)',
      'font-family:Inter,system-ui,sans-serif;font-size:14px;line-height:1.5'
    ].join(';');

    state.overlay.appendChild(state.modal);
    document.body.appendChild(state.overlay);

    // Click outside to cancel
    state.overlay.addEventListener('click', function (e) {
      if (e.target === state.overlay) handleCancel();
    });
    // Escape to cancel
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.overlay.style.pointerEvents === 'auto') handleCancel();
    });
  }

  function show() {
    ensureModal();
    state.overlay.style.opacity = '1';
    state.overlay.style.pointerEvents = 'auto';
  }

  function hide() {
    if (!state.overlay) return;
    state.overlay.style.opacity = '0';
    state.overlay.style.pointerEvents = 'none';
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  function renderLabs() {
    var clinicMap = {};
    (state.currentClinics || []).forEach(function (c) {
      clinicMap[c.id] = c;
      clinicMap[normalizeLabId(c.id)] = c;
      if (c.name) clinicMap[normalizeLabId(c.name)] = c;
    });

    var labsHtml = '';
    state.currentLabs.forEach(function (labId) {
      var clinic = clinicMap[labId] || clinicMap[normalizeLabId(labId)];
      var clinicName = clinic ? clinic.name : labId;
      var branches = clinic ? (clinic.branches || []) : [];
      var isSelected = state.selectedLab === labId;

      labsHtml += '<div style="margin-bottom:14px;border:1px solid ' + (isSelected ? '#0a4f57' : '#d9e3e2') +
        ';border-radius:10px;padding:12px 14px;cursor:pointer;transition:border-color .15s;"' +
        ' data-lab-option="' + labId + '">';

      labsHtml += '<label style="display:flex;align-items:center;gap:10px;cursor:pointer;font-weight:600;color:#0d2a2f;">';
      labsHtml += '<input type="radio" name="nx-lab-select" value="' + labId + '"' +
        (isSelected ? ' checked' : '') +
        ' style="accent-color:#0a4f57;width:16px;height:16px;flex-shrink:0;">';
      labsHtml += clinicName + '</label>';

      // Only show branch details if geolocation is granted — nearest branch only
      if (state.geoCoords && branches.length > 0) {
        var nearestBranch = null;
        var nearestDist = Infinity;
        branches.forEach(function (branch) {
          var dist = haversineDistance(
            state.geoCoords.latitude, state.geoCoords.longitude,
            branch.lat, branch.lng
          );
          if (dist < nearestDist) { nearestDist = dist; nearestBranch = branch; }
        });
        if (nearestBranch) {
          labsHtml += '<div style="margin:8px 0 0 26px;">';
          labsHtml += '<div style="font-size:13px;font-weight:500;color:#3a4d50;">' +
            nearestBranch.name +
            ' <span style="color:#6e8489;font-size:11px;">' + nearestDist.toFixed(1) + ' km away</span></div>';
          if (nearestBranch.address) {
            labsHtml += '<div style="font-size:11px;color:#6e8489;margin-top:2px;">' + nearestBranch.address + '</div>';
          }
          labsHtml += '</div>';
        }
      }

      labsHtml += '</div>';
    });

    var html = '<h3 style="margin:0 0 16px;font-size:17px;font-weight:700;color:#0d2a2f;">Choose a Lab</h3>';
    html += labsHtml;
    html += '<div style="display:flex;gap:8px;margin-top:18px;padding-top:14px;border-top:1px solid #d9e3e2;">';
    html += '<button type="button" id="nx-lab-nearest-btn"' +
      ' style="flex:1;padding:10px 12px;border:1px solid #d9e3e2;border-radius:8px;background:#fff;' +
      'cursor:pointer;font-size:13px;font-weight:600;font-family:inherit;">&#128205; Select Nearest</button>';
    html += '<button type="button" id="nx-lab-confirm-btn"' +
      ' style="flex:1;padding:10px 12px;border:none;border-radius:8px;background:#0a4f57;color:#fff;' +
      'cursor:pointer;font-size:13px;font-weight:600;font-family:inherit;">Confirm</button>';
    html += '<button type="button" id="nx-lab-cancel-btn"' +
      ' style="padding:10px 12px;border:1px solid #d9e3e2;border-radius:8px;background:#fff;' +
      'cursor:pointer;font-size:13px;font-family:inherit;">Cancel</button>';
    html += '</div>';

    state.modal.innerHTML = html;

    // Radio change — update selectedLab and re-render borders
    state.modal.querySelectorAll('input[type="radio"]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        state.selectedLab = radio.value;
        state.selectedBranchIdx = -1; // clear if manually selected
        state.modal.querySelectorAll('[data-lab-option]').forEach(function (opt) {
          opt.style.borderColor = opt.dataset.labOption === state.selectedLab ? '#0a4f57' : '#d9e3e2';
        });
      });
    });

    // Clicking the option card also selects it
    state.modal.querySelectorAll('[data-lab-option]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        var r = opt.querySelector('input[type="radio"]');
        if (r) { r.checked = true; r.dispatchEvent(new Event('change')); }
      });
    });

    state.modal.querySelector('#nx-lab-nearest-btn').addEventListener('click', handleSelectNearest);
    state.modal.querySelector('#nx-lab-confirm-btn').addEventListener('click', handleConfirm);
    state.modal.querySelector('#nx-lab-cancel-btn').addEventListener('click', handleCancel);
  }

  // ── Geolocation ─────────────────────────────────────────────────────────────

  function handleSelectNearest() {
    if (state.geoCoords) {
      applyNearest();
      return;
    }
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }
    var nearestBtn = state.modal.querySelector('#nx-lab-nearest-btn');
    if (nearestBtn) nearestBtn.textContent = 'Locating…';

    navigator.geolocation.getCurrentPosition(function (pos) {
      state.geoCoords = pos.coords;
      state.geoGranted = true;
      renderLabs(); // re-render with distances
      applyNearest();
    }, function () {
      state.geoGranted = false;
      renderLabs(); // re-render (no distances)
      alert('Location access denied. Please select a lab manually.');
    }, { enableHighAccuracy: true, timeout: 8000 });
  }

  function applyNearest() {
    if (!state.geoCoords) return;

    var nearestLab = null;
    var nearestBranchIdx = -1;
    var nearestDist = Infinity;

    state.currentLabs.forEach(function (labId) {
      var clinic = (state.currentClinics || []).find(function (c) { return c.id === labId; });
      if (!clinic) return;
      (clinic.branches || []).forEach(function (branch, idx) {
        var dist = haversineDistance(
          state.geoCoords.latitude, state.geoCoords.longitude,
          branch.lat, branch.lng
        );
        if (dist < nearestDist) { 
          nearestDist = dist; 
          nearestLab = labId; 
          nearestBranchIdx = idx;
        }
      });
    });

    if (nearestLab) {
      state.selectedLab = nearestLab;
      state.selectedBranchIdx = nearestBranchIdx;
      var radio = state.modal.querySelector('input[value="' + nearestLab + '"]');
      if (radio) radio.checked = true;
      state.modal.querySelectorAll('[data-lab-option]').forEach(function (opt) {
        opt.style.borderColor = opt.dataset.labOption === nearestLab ? '#0a4f57' : '#d9e3e2';
      });
      handleConfirm();
    }
  }

  // ── Confirm / Cancel ────────────────────────────────────────────────────────

  function handleConfirm() {
    if (!state.selectedLab) {
      alert('Please choose a lab before confirming.');
      return;
    }
    var cb = state.onSelect;
    var lab = state.selectedLab;
    var bIdx = state.selectedBranchIdx !== -1 ? state.selectedBranchIdx : undefined;
    hide();
    state.selectedLab = null;
    state.selectedBranchIdx = -1;
    state.onSelect = null;
    if (cb) cb(lab, bIdx);
  }

  function handleCancel() {
    hide();
    state.selectedLab = null;
    state.selectedBranchIdx = -1;
    state.onSelect = null;
  }

  function normalizeLabId(str) {
    if (!str) return '';
    var s = String(str).toLowerCase().trim();
    if (s === 'hp' || s.indexOf('hi-precision') !== -1 || s.indexOf('hiprecision') !== -1) return 'hi-precision';
    return s.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function adaptPortalBranches(portalBranches, staticClinics) {
    var clinicMap = {};
    (staticClinics || []).forEach(function (c) {
      var normId = normalizeLabId(c.id || c.name);
      clinicMap[normId] = { id: c.id || normId, name: c.name, branches: (c.branches || []).slice() };
    });

    if (portalBranches && portalBranches.length > 0) {
      // Group portal branches by normalized vendor code
      var portalGroups = {};
      portalBranches.forEach(function (b) {
        var normId = normalizeLabId(b.vendorCode || b.vendorName || b.name);
        if (!portalGroups[normId]) portalGroups[normId] = [];
        portalGroups[normId].push({
          id: b.id,
          code: b.code || b.vendorBranchCode,
          name: b.name,
          lat: b.latitude !== undefined ? b.latitude : b.lat,
          lng: b.longitude !== undefined ? b.longitude : b.lng,
          address: b.address || b.area || '',
          area: b.area || 'Metro Manila',
          hours: b.hours || 'Mon-Sat · 6 AM-5 PM'
        });
      });

      // Override or add clinic branches from portal
      for (var normId in portalGroups) {
        if (clinicMap[normId]) {
          clinicMap[normId].branches = portalGroups[normId];
        } else {
          var firstRaw = portalBranches.find(function (x) { return normalizeLabId(x.vendorCode || x.vendorName || x.name) === normId; });
          var displayName = firstRaw && firstRaw.vendorCode === 'HP' ? 'Hi-Precision' : ((firstRaw && (firstRaw.vendorName || firstRaw.name)) || normId);
          clinicMap[normId] = { id: normId, name: displayName, branches: portalGroups[normId] };
        }
      }
    }

    var result = [];
    for (var key in clinicMap) {
      result.push(clinicMap[key]);
    }
    return result;
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  window.NXLabPicker = {
    open: function (opts) {
      state.currentLabs = opts.partnerLabs || [];
      var portal = opts.portalBranches || window.__NX_PORTAL_BRANCHES;
      /* Static clinics fallback commented out: prioritize portalBranches first
      var staticClinics = (opts.clinicDirectory && opts.clinicDirectory.clinics) || [];
      */
      var staticClinics = [];
      state.currentClinics = adaptPortalBranches(portal, staticClinics);
      state.selectedLab = null;
      state.selectedBranchIdx = -1;
      state.onSelect = opts.onSelect || null;
      ensureModal(); // must run before renderLabs() so state.modal exists
      renderLabs();
      show();
    },
    close: handleCancel
  };
})();
